import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { Star, Search } from 'lucide-react';
import { categories, getAllTools } from '../data/tools';
import { Card, CardContent, CardDescription, CardTitle } from './ui/Card';
import { useLanguageRouter } from '../utils/i18nRouter';
import { searchTools } from '../utils/searchUtils';
import { SearchHighlight } from './SearchHighlight';
import { SearchResultsHeader } from './SearchResultsHeader';
import { SearchResultCard } from './SearchResultCard';
import { useSearch } from '../contexts/SearchContext';

interface HomeContentProps {
  searchQuery?: string;
}

export const HomeContent: React.FC<HomeContentProps> = ({ searchQuery: propSearchQuery = '' }) => {
  const { t } = useTranslation();
  const { getLocalizedPath } = useLanguageRouter();
  const { searchQuery: contextSearchQuery } = useSearch();
  const allTools = getAllTools();

  // 使用context中的searchQuery，如果没有则使用props中的
  const searchQuery = contextSearchQuery || propSearchQuery;



  const searchResults = searchQuery && searchQuery.trim()
    ? searchTools(allTools, searchQuery, t, {
        fuzzy: true,
        caseSensitive: false,
        minLength: 1,
        multiKeywordStrategy: 'all', // 要求所有关键词都匹配，提供更精确的结果
        weights: {
          name: 3,
          description: 2,
          keywords: 2,
          longDescription: 1,
        }
      })
    : [];

  const filteredTools = searchResults.map(result => result.tool);

  const colorClasses = {
    blue: 'from-blue-500 to-blue-600',
    green: 'from-green-500 to-green-600',
    purple: 'from-purple-500 to-purple-600',
    orange: 'from-orange-500 to-orange-600',
    pink: 'from-pink-500 to-pink-600',
    indigo: 'from-indigo-500 to-indigo-600',
    teal: 'from-teal-500 to-teal-600',
    cyan: 'from-cyan-500 to-cyan-600',
    red: 'from-red-500 to-red-600',
  };





  if (searchQuery && searchQuery.trim()) {
    // Group filtered tools by category
    const toolsByCategory = categories.map(category => ({
      ...category,
      tools: filteredTools.filter(tool => tool.category === category.id)
    })).filter(category => category.tools.length > 0);

    return (
      <div className="p-6 space-y-8">
        {/* Search Results Header */}
        <SearchResultsHeader
          searchQuery={searchQuery}
          resultCount={filteredTools.length}
        />

        {toolsByCategory.length > 0 ? (
          <div className="space-y-8">
            {toolsByCategory.map((category) => {
              const IconComponent = category.icon;
              const gradientClass = colorClasses[category.color as keyof typeof colorClasses];

              return (
                <div key={category.id} className="space-y-6">
                  {/* Category Header */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className={`inline-flex items-center px-4 py-2 rounded-full bg-gradient-to-r ${gradientClass} text-white font-medium shadow-lg`}>
                        <IconComponent className="w-5 h-5 mr-2" />
                        {t(`categories.${category.id}`)}
                      </div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">
                        {category.tools.length} {t('home.toolsFound')}
                      </div>
                    </div>
                  </div>

                  {/* Tools Grid */}
                  <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                    {category.tools.map((tool) => {
                      // 找到对应的搜索结果以获取分数和匹配字段
                      const searchResult = searchResults.find(result => result.tool.id === tool.id);
                      const globalRank = searchResults.findIndex(result => result.tool.id === tool.id) + 1;

                      return (
                        <SearchResultCard
                          key={tool.id}
                          tool={tool}
                          searchQuery={searchQuery}
                          matchedFields={searchResult?.matchedFields || []}
                          score={searchResult?.score || 0}
                          rank={globalRank}
                        />
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-20 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900 rounded-2xl border border-gray-200 dark:border-gray-700">
            <div className="mb-8">
              <div className="w-24 h-24 bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-6">
                <Search className="w-12 h-12 text-gray-400 dark:text-gray-500" />
              </div>
              <h3 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
                {t('home.noResultsFound')}
              </h3>
              <p className="text-gray-600 dark:text-gray-400 text-lg max-w-lg mx-auto leading-relaxed">
                {t('home.tryDifferentKeywords')}
              </p>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 max-w-2xl mx-auto border border-gray-200 dark:border-gray-700 shadow-sm">
              <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                💡 Search Suggestions
              </h4>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                {t('home.searchSuggestion')}
              </p>
              <div className="flex flex-wrap gap-2 justify-center">
                {['JSON', 'Base64', 'URL', 'QR Code', 'Password', 'Hash'].map((suggestion) => (
                  <button
                    key={suggestion}
                    onClick={() => {
                      // This would trigger a new search
                      // For now, it's just a visual element
                    }}
                    className="px-3 py-1 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 rounded-full text-sm font-medium hover:bg-primary-200 dark:hover:bg-primary-900/50 transition-colors cursor-pointer"
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": t('header.title'),
    "description": t('home.hero.description'),
    "url": window.location.origin,
    "applicationCategory": "DeveloperApplication",
    "operatingSystem": "Any",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    },
    "featureList": [
      t('home.hero.title'),
      t('home.hero.subtitle'),
      t('home.categories.title')
    ],
    "provider": {
      "@type": "Organization",
      "name": t('header.title')
    }
  };

  return (
    <div className="p-6 space-y-12">
      <Helmet>
        <title>{t('header.title')} - {t('header.subtitle')}</title>
        <meta name="description" content={t('home.hero.description')} />
        <meta name="keywords" content="online tools, developer tools, free tools, web tools, utilities" />
        <meta property="og:title" content={`${t('header.title')} - ${t('header.subtitle')}`} />
        <meta property="og:description" content={t('home.hero.description')} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={window.location.origin} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${t('header.title')} - ${t('header.subtitle')}`} />
        <meta name="twitter:description" content={t('home.hero.description')} />
        <link rel="canonical" href={window.location.origin} />
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      {/* Tool Categories */}
      <section id="tool-categories">
        <div className="space-y-12">
          {categories.map((category) => {
            const IconComponent = category.icon;
            const gradientClass = colorClasses[category.color as keyof typeof colorClasses];

            return (
              <div key={category.id} className="space-y-6">
                {/* Category Header */}
                <div className="flex items-center space-x-4">
                  <div className={`inline-flex items-center px-4 py-2 rounded-full bg-gradient-to-r ${gradientClass} text-white font-medium`}>
                    <IconComponent className="w-5 h-5 mr-2" />
                    {t(`categories.${category.id}`)}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    {t(`categories.${category.id}.description`)}
                  </div>
                </div>

                {/* Tools Grid */}
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                  {category.tools.map((tool) => {
                    const ToolIcon = tool.icon;
                    return (
                      <Link
                        key={tool.id}
                        to={getLocalizedPath(tool.path)}
                        className="group"
                      >
                        <Card className="h-full hover:shadow-lg transition-all duration-200 hover:border-primary-300 dark:hover:border-primary-600 hover:-translate-y-1">
                          <CardContent className="p-4">
                            <div className="flex items-start space-x-3">
                              <div className="w-10 h-10 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-primary-100 dark:group-hover:bg-primary-900/20 transition-colors">
                                <ToolIcon className="w-5 h-5 text-gray-600 dark:text-gray-300 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <CardTitle className="text-sm mb-1 line-clamp-1 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                                  <SearchHighlight
                                    text={t(tool.name)}
                                    searchTerm={searchQuery}
                                  />
                                </CardTitle>
                                <CardDescription className="text-xs line-clamp-2">
                                  <SearchHighlight
                                    text={t(tool.description)}
                                    searchTerm={searchQuery}
                                  />
                                </CardDescription>
                                {tool.featured && (
                                  <div className="flex items-center mt-2">
                                    <Star className="w-3 h-3 text-yellow-500 mr-1" />
                                    <span className="text-xs text-yellow-600 dark:text-yellow-400 font-medium">
                                      {t('home.featured')}
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      </Link>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </section>


    </div>
  );
};
